import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Box, Card, Checkbox, Grid, Typography } from "@mui/material";
import { useAuth } from "@sal/authentication.react";
import ResponsiveButton from "components/ResponsiveButton";
import ActionCell from "components/personnelAndFleet/common/ActionCell";
import ChevronButton from "components/personnelAndFleet/common/ChevronButton";
import DetailCell from "components/personnelAndFleet/common/DetailCell";
import StatusCell from "components/personnelAndFleet/common/StatusCell";
import CommunicationsCell from "components/personnelAndFleet/personnel/CommunicationsCell";
import { PERSONNEL_STATUS_LABELS, PersonnelStatus } from "enums/PersonnelEnums";
import { useGetRoles } from "hooks/roles";
import { PersonnelUser } from "models/PersonnelUser";
import { useMemo } from "react";
import { getPersonnelUserActionItems } from "./personnelUtils";

type PersonnelCardListProps = {
	rows: PersonnelUser[];
	selectedRows: Set<number>;
	setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
	selectionDisabled?: boolean;
	onEditUser: (user: PersonnelUser) => void;
	onDuplicateUser: (user: PersonnelUser) => void;
	onDeactivateUser: (user: PersonnelUser) => void;
	onActivateUser: (user: PersonnelUser) => void;
	onApproveUser: (user: PersonnelUser) => void;
	canWrite?: boolean;
};

export default function PersonnelCardList({
	rows,
	selectedRows,
	setSelectedRows,
	selectionDisabled = false,
	onEditUser,
	onDuplicateUser,
	onDeactivateUser,
	onActivateUser,
	onApproveUser,
	canWrite = false
}: PersonnelCardListProps) {
	const auth = useAuth();
	const canReadRoles = auth.userHasPermission("Roles", "Read");
	const { roleOptions } = useGetRoles(canReadRoles);

	const sortedRows = useMemo(
		() => [...rows].sort((a, b) => (a.firstName ?? "").localeCompare(b.firstName ?? "")),
		[rows]
	);

	const getRoleLabels = (roles: string[]) =>
		roles.map((v) => roleOptions.find((o) => o.value === v)?.label ?? v).join(", ");

	const handleToggleRow = (rowId: number) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);
			next.has(rowId) ? next.delete(rowId) : next.add(rowId);
			return next;
		});
	};

	if (rows.length === 0) {
		return (
			<Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
				<Typography variant="text14">No personnel found.</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				overflowY: "auto",
				gap: 1.5,
				backgroundColor: "common.white",
				p: 2
			}}
		>
			{sortedRows.map((row) => {
				const isSelected = selectedRows.has(row.id);
				const isPending = !row.cvWhenApproved;
				const fullName = [row.firstName, row.lastName].filter(Boolean).join(" ") || "-";
				const actionItems = getPersonnelUserActionItems(row, {
					onEditUser,
					onDuplicateUser,
					onDeactivateUser,
					onActivateUser,
					onApproveUser
				});

				return (
					<Card
						key={row.id}
						variant="outlined"
						sx={{
							display: "flex",
							flexShrink: 0,
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							gap: "7px", // to align with checkbox
							p: 2,
							pl: { sm: "7px" }, // to align with checkbox when it is present
							borderWidth: 2,
							borderColor: isSelected ? "text.primary" : "border.hover",
							borderRadius: 1.5,
							boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)"
						}}
					>
						{/* Checkbox — visible on sm+ (left side) */}
						<Box sx={{ display: { xs: "none", sm: "flex" } }}>
							<Checkbox
								checked={isSelected}
								disabled={selectionDisabled}
								onChange={() => handleToggleRow(row.id)}
							/>
						</Box>
						{/* Rows wrapper — stacks name above details */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1.5,
								flex: "1 0 0",
								minWidth: 0
							}}
						>
							{/* Row 1: Name + contact info (+ checkbox on xs) */}
							<Box sx={{ position: "relative" }}>
								<Box
									sx={{
										flex: "1 0 0",
										minWidth: 0,
										display: "flex",
										flexDirection: "column",
										gap: 0.75
									}}
								>
									<Typography
										variant="text16Bold"
										sx={{ wordBreak: "break-word", mr: 4, minHeight: "24px" }}
									>
										{fullName}
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: { xs: "column", sm: "row" },
											flexWrap: "wrap",
											alignItems: { xs: "flex-start", sm: "center" },
											gap: { xs: 0.25, sm: 0.75 },
											minWidth: 0,
											maxWidth: "100%"
										}}
									>
										{row.email && (
											<Typography
												variant="text14"
												sx={{
													minWidth: 0,
													maxWidth: "100%",
													wordBreak: "break-word",
													overflowWrap: "anywhere"
												}}
											>
												{row.email}
											</Typography>
										)}
										{row.email && row.fullPhone && (
											<FiberManualRecordIcon
												sx={{
													fontSize: 4,
													color: "text.primary",
													display: { xs: "none", sm: "block" },
													flexShrink: 0
												}}
											/>
										)}
										{row.fullPhone && (
											<Typography variant="text14" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
												{row.fullPhone}
											</Typography>
										)}
									</Box>
								</Box>

								{/* Checkbox — visible on xs only (top-right) */}
								<Box
									sx={{
										display: { xs: "flex", sm: "none" },
										position: "absolute",
										top: -12,
										right: -12
									}}
								>
									<Checkbox
										checked={isSelected}
										disabled={selectionDisabled}
										onChange={() => handleToggleRow(row.id)}
									/>
								</Box>
							</Box>

							{/* Row 2: Details (+ chevron on xs only) */}
							<Box
								sx={{
									display: "flex",
									gap: { xs: 1.25, sm: 1.5 },
									minWidth: 0
								}}
							>
								<Grid container spacing={{ xs: 0.5, sm: 1.5 }} sx={{ flex: "1 0 0", minWidth: 0 }}>
									<DetailCell
										label="Role(s)"
										value={row.roles?.length ? getRoleLabels(row.roles) : "-"}
									/>
									<DetailCell label="Status">
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<StatusCell
												isPending={isPending}
												isActive={row.active === true}
												label={
													isPending
														? "Pending Approval"
														: row.active !== undefined
															? PERSONNEL_STATUS_LABELS[
																	row.active ? PersonnelStatus.ACTIVE : PersonnelStatus.DEACTIVATED
																]
															: "-"
												}
											/>
										</Box>
									</DetailCell>

									<DetailCell label="Communications">
										<CommunicationsCell preferences={row.communicationPreferences} />
									</DetailCell>

									{isPending && canWrite && (
										<Grid
											size={{ xs: 12, sm: 6 }}
											offset={{ sm: 6 }}
											sx={{ display: "flex", alignItems: "center", marginTop: { xs: 1, sm: 0 } }}
										>
											<Box sx={{ display: "flex", flexGrow: 1 }}>
												<ResponsiveButton
													size="small"
													variant="contained"
													onClick={() => onApproveUser(row)}
												>
													Approve
												</ResponsiveButton>
											</Box>
										</Grid>
									)}
								</Grid>

								{/* Chevron — visible on xs only, hidden when pending or read-only */}
								{!isPending && canWrite && (
									<ActionCell
										items={actionItems}
										trigger={(onClick) => <ChevronButton onClick={onClick} />}
										sx={{ display: { xs: "flex", sm: "none" }, mr: -1.5 }}
									/>
								)}
							</Box>
						</Box>

						{/* Chevron — visible on sm+, hidden when pending or read-only */}
						{!isPending && canWrite && (
							<ActionCell
								items={actionItems}
								trigger={(onClick) => <ChevronButton onClick={onClick} />}
								sx={{ display: { xs: "none", sm: "flex" }, alignSelf: "stretch", mr: -1.5 }}
							/>
						)}
					</Card>
				);
			})}
		</Box>
	);
}
